import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Card, Divider, MarkdownContainer, StatusBadge } from '../../styles/PageStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faArrowDown, faArrowLeft, faArrowRight, faArrowUp, faCircleExclamation, faRefresh, faSpinner, faTimes, faPlay } from '@fortawesome/free-solid-svg-icons';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const NodeExecutionCard = ({ result, index }) => {
  const getStatusIcon = () => {
    switch (result.transaction_status) {
      case 'CLOSED':
        return <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-green-500" />;
      case 'error':
        return <FontAwesomeIcon icon={faCircleExclamation} className="w-5 h-5 text-red-500" />;
      case 'INPROGRESS':
        return <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (result.transaction_status) {
      case 'CLOSED':
        return 'bg-green-50 border-green-200';
      case 'ERRORED':
        return 'bg-red-50 border-red-200';
      case 'INPROGRESS':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      {index > 0 && (
        <div className="flex justify-center">
          {/* <FontAwesomeIcon icon={faArrowDown} className="w-4 h-4 text-gray-400" /> */}
          &nbsp;
        </div>
      )}
      <Card className={`p-4 ${getStatusColor()} transition-all duration-300 hover:shadow-md`}>
        <div className="space-y-3">
          {/* Node Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              {getStatusIcon()}
              <div>
                <h4 className="font-semibold text-gray-900 text-size-rem-1_1">{result.name}</h4>
                <div className="flex items-center space-x-2 mt-1">
                  <StatusBadge variant="secondary" className="text-xs">
                    {result.type}
                  </StatusBadge>
                  <div className="flex items-center text-xs text-gray-500">
                    <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                    {formatExecutionTime(getExecutionTime(result.created_at, result.updated_at))}
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 min-width-80">
              {formatDateTime(result.updated_at)}
            </div>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <div className="text-sm text-gray-700 leading-relaxed">
              <MarkdownContainer>
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {result.summary || '*No decision summary available.*'}
                  </ReactMarkdown>
                </div>
              </MarkdownContainer>
            </div>
          </div>
          {result.entities && result.entities.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-gray-900">Extracted Entities</h5>
              <div className="flex flex-wrap gap-2">
                {result.entities.map((entity, idx) => (
                  <div
                    key={idx ? idx : `entity-${result.nodeId || result.id}-${idx}`}
                    className="bg-white rounded-lg px-2 py-1 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium text-gray-600">{entity.type}</span>
                      <span className="text-xs text-gray-900">{entity.value}</span>
                      <StatusBadge variant="outline" className="text-xs h-4">
                        {Math.round(entity.confidence * 100)}%
                      </StatusBadge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(result.inputData || result.outputData) && (
            <div className="space-y-3">
              <Divider />
              <div className="grid grid-cols-1 gap-3">
                {result.inputData && (
                  <div>
                    <h6 className="text-xs font-medium text-gray-600 mb-2">Input Data</h6>
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(result.inputData, null, 2)}
                    </pre>
                  </div>
                )}
                {result.outputData && (
                  <div>
                    <h6 className="text-xs font-medium text-gray-600 mb-2">Output Data</h6>
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(result.outputData, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
const getExecutionTime = (startDate, endDate) => {
  // Handle the case where dates are not defined
  if (!startDate || !endDate) {
    console.warn('Missing start or end date for execution time calculation', { startDate, endDate });
    return 0;
  }

  // Handle UTC conversion properly
  let start, end;
  
  if (typeof startDate === 'string') {
    if (startDate.endsWith('Z')) {
      start = new Date(startDate);
    } else {
      // Explicitly treat as UTC if no Z suffix
      start = new Date(startDate + 'Z');
    }
  } else {
    start = new Date(startDate);
  }
  
  if (typeof endDate === 'string') {
    if (endDate.endsWith('Z')) {
      end = new Date(endDate);
    } else {
      // Explicitly treat as UTC if no Z suffix
      end = new Date(endDate + 'Z');
    }
  } else {
    end = new Date(endDate);
  }

  // Check for valid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.error('Invalid date in execution time calculation', { 
      startDate, endDate, 
      parsedStart: start, 
      parsedEnd: end 
    });
    return 0;
  }

  // Make sure end is after start
  if (end < start) {
    console.warn('End date is before start date in execution time calculation', { 
      startDate, endDate, 
      parsedStart: start.toISOString(), 
      parsedEnd: end.toISOString() 
    });
    return 0;
  }

  return end - start; // returns time in milliseconds
};

const formatExecutionTime = (milliseconds) => {
  // Handle invalid inputs gracefully
  if (milliseconds === undefined || milliseconds === null || isNaN(milliseconds)) {
    console.warn('Invalid milliseconds value passed to formatExecutionTime:', milliseconds);
    return '0ms';
  }
  
  // Ensure milliseconds is a number
  const ms = Number(milliseconds);
  
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`;
  } else {
    return `${(ms / 60000).toFixed(1)}m`;
  }
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return 'N/A';
  
  let date;
  
  // Handle UTC conversion properly
  if (dateValue instanceof Date) {
    date = new Date(dateValue);
  } else if (typeof dateValue === 'string') {
    // Make sure we're treating the server date as UTC
    if (dateValue.endsWith('Z')) {
      // If the date already has a 'Z' suffix indicating UTC, use it directly
      date = new Date(dateValue);
    } else {
      // If the date doesn't specify a timezone, explicitly treat it as UTC
      date = new Date(dateValue + 'Z');
    }
  } else {
    date = new Date(dateValue);
  }
  
  // Check for valid date
  if (isNaN(date.getTime())) {
    console.error('Invalid date in formatDateTime:', dateValue);
    return 'Invalid Date';
  }
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };

  // If it's today, just show time
  if (compareDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString(undefined, timeOptions);
  }

  // If it's a different day, show day and month with time
  const dayOptions = {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };

  return date.toLocaleString(undefined, dayOptions);
};
const PipelineExecutionDrawer = ({
  isOpen,
  onClose,
  onRefresh,
  onResume,
  onPaginationChange,
  pipelineStatus,
  executionResults,
  totalExecutionResults,
  isRunning,
  isLoadingMore,
  pipelineName,
  showCloseButton = false
}) => {
  // Compute total execution time with thorough validation
  const totalExecutionTime = executionResults.reduce((sum, result, index) => {
    // Skip nodes without both start and end dates
    if (!result.created_at || !result.updated_at) {
      console.warn(`Node ${index} (${result.name}) missing timestamps:`, {
        created_at: result.created_at,
        updated_at: result.updated_at
      });
      return sum;
    }

    // For API-based nodes, we can have start/end dates that are exact same timestamp
    // In this case, assign a minimal execution time (100ms) for visualization purposes
    if (result.created_at === result.updated_at) {
      console.debug(`Node ${index} (${result.name}) has identical timestamps, assigning default duration`);
      return sum + 100; // Add 100ms as minimal execution time
    }
    
    const executionTime = getExecutionTime(result.created_at, result.updated_at);
    
    // Debug log for first few results to avoid console flooding
    if (index < 3) {
      console.debug(`Node ${index} execution time:`, {
        name: result.name,
        startDate: result.created_at,
        endDate: result.updated_at,
        duration: executionTime,
        durationFormatted: formatExecutionTime(executionTime)
      });
    }
    
    if (executionTime <= 0) {
      console.warn(`Node ${index} (${result.name}) has zero or negative execution time:`, {
        startDate: result.created_at,
        endDate: result.updated_at,
        calculatedTime: executionTime
      });
      return sum + 100; // Add minimal execution time for visualization
    }
    
    return sum + executionTime;
  }, 0);
  
  const successCount = executionResults.filter(r => r.transaction_status === 'CLOSED').length;
  const errorCount = executionResults.filter(r => r.transaction_status !== 'CLOSED').length;
  const scrollRef = useRef(null);
  const nodeContainerRef = useRef(null);
  // Get the current status for display
  const getCurrentStatus = () => {
    if (typeof pipelineStatus === 'string') {
      return pipelineStatus;
    } else if (pipelineStatus?.transaction_status) {
      return pipelineStatus.transaction_status;
    }
    return 'Unknown';
  };

  // Check if pipeline can be resumed
  const canResumeStatus = () => {
    const status = getCurrentStatus();
    return status && ['WAITING', 'ERRORED', 'ERROR'].includes(status.toUpperCase());
  };


  // const bubbleUpPagination = useCallback((event) => {
  //   if (onPaginationChange && !isLoadingMore) {
  //     onPaginationChange(event);
  //   }
  // }, [onPaginationChange, isLoadingMore]);

  useEffect(() => {
    // Only auto-scroll to bottom on initial load or when drawer opens
    if (nodeContainerRef.current && executionResults.length > 0 && !isLoadingMore) {
      setTimeout(() => {
        const container = nodeContainerRef.current;
        // Check if container still exists before accessing scrollHeight
        if (container && executionResults.length) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [isOpen, executionResults.length]); // i have added length dep due to length is dynamically changing.

  // Scroll event handler for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!nodeContainerRef.current || isLoadingMore) return;

      const container = nodeContainerRef.current;
      // Additional safety checks
      if (!container || container.scrollTop === undefined || !container.scrollHeight || !container.clientHeight) return;

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if scrolled to bottom (within 50px threshold)
      if (scrollHeight - scrollTop - clientHeight < 50) {
        // console.log('Scrolled to bottom, requesting more data...');
        if (onPaginationChange && typeof onPaginationChange === 'function') {
          onPaginationChange('next');
        }
      }
    };

    const container = nodeContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        // Additional safety check on cleanup
        if (container && container.removeEventListener) {
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [isLoadingMore, onPaginationChange]);

  // Debug effect to track pipeline status changes
  useEffect(() => {
    // console.log('Pipeline status changed:', pipelineStatus, typeof pipelineStatus);
  }, [pipelineStatus]);
  
  // Debug effect to show time conversion example when drawer opens
  useEffect(() => {
    if (isOpen && executionResults.length > 0) {
      const serverTime = executionResults[0].created_at;
      const localTime = new Date(serverTime);
      
    }
  }, [isOpen, executionResults, totalExecutionTime]);


  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-50 flex">
      <div
        className="flex-1 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="w-full bg-white shadow-2xl transform transition-transform duration-300 ease-out h100dvh">
        <div className="h-full flex flex-col">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{pipelineName}</h2>
                <p className="text-blue-100 text-sm mt-1">
                  Status: {getCurrentStatus()}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefresh}
                  className="text-white onBlueBtn"
                >
                  <FontAwesomeIcon icon={faRefresh} className="w-5 h-5" />
                </Button>
                {canResumeStatus() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onResume}
                    className="text-white onBlueBtn"
                    title="Resume Pipeline"
                  >
                    <FontAwesomeIcon icon={faPlay} className="w-5 h-5" />
                  </Button>
                )}
                {showCloseButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-white onBlueBtn"
                    title="Close Drawer"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{totalExecutionResults}</div>
                <div className="text-xs text-blue-100">Nodes</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{formatExecutionTime(totalExecutionTime)}</div>
                <div className="text-xs text-blue-100">Total Time</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-300">{successCount}</div>
                <div className="text-xs text-blue-100">Success</div>
              </div>
            </div>
          </div>
          <div className="flex-1 p-6 min-h-0 p-r-0" ref={scrollRef}>
            {isRunning && executionResults.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
                  <p className="text-gray-600">Fetching Records...</p>
                </div>
              </div>
            )}
            {executionResults.length > 0 && (
              <div
                className="space-y-1 scorllControl"
                ref={nodeContainerRef}
              >
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Execution Timeline</h3>
                {executionResults.map((result, index) => (
                  <NodeExecutionCard
                    key={result.nodeId || result.id || `node-${index}`}
                    data-card-index={index}
                    result={result}
                    index={index}
                  />
                ))}

                {/* {isLoadingMore && (
                  <div className="flex justify-center py-4 sec1">
                    <div className="text-center">
                      <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Loading more results...</p>
                          {console.log('Total execution results3:')}

                      <p className="text-xs text-gray-500">
                        {executionResults.length} of {totalExecutionResults || '?'} nodes loaded
                      </p>
                    </div>
                  </div>
                )} */}
                {isRunning && executionResults.length > 0 && (
                  <div className="flex justify-center py-4 sec2">
                    <div className="text-center">
                      <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Processing next node...</p>
                    </div>
                  </div>
                )}

                {/* {!isRunning && !isLoadingMore && executionResults.length > 0 && totalExecutionResults &&
                  executionResults.length >= totalExecutionResults && (
                    <div className="flex justify-center py-4 sec3">
                      <div className="text-center">
                        <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
                          {totalExecutionResults > 0 && (
                            <>
                              All {totalExecutionResults} nodes loaded
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )} */}
              </div>
            )}
            {/* {!isRunning && executionResults.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FontAwesomeIcon icon={faClock} className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600">No execution data available</p>
                  <p className="text-sm text-gray-500 mt-1">Run a pipeline to see analytics</p>
                </div>
              </div>
            )} */}
          </div>
          {/* {executionResults.length > 0 && !isRunning && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Execution completed at {new Date().toLocaleTimeString()}
                </div>
                <StatusBadge variant={errorCount > 0 ? "destructive" : "default"}>
                  {errorCount > 0 ? `${errorCount} errors` : 'Success'}
                </StatusBadge>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default PipelineExecutionDrawer;
